import { useState, useEffect } from "react";
import Loader from "components/Loader";
import HelpCard from "components/Cards/HelpCard";
import { Button, Col, Form } from "react-bootstrap";
import Validator from "utils/Validator";
import classNames from "classnames";
import { css } from "aphrodite";
import pageStyles from "styles/pageStyles";
import { getInputFieldStyle, getButtonStyle } from "utils/formStyles";
import InsufficientCreditsPopup from "components/InsufficientCreditsPopup";
import ToastAlert from "components/Alert/ToastAlert";
import { submitAiOverviewByUrlRequest } from "services/buddyXApiHubService";
import AiOverviewTable from "components/MarkdownRender/tables/AiOverviewTable";

export default function Page() {
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResultGot, setIsResultGot] = useState(false);
  const [showCopyNotification, setShowCopyNotification] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [url, setUrl] = useState("");
  const [noQueries, setNoQueries] = useState(10);
  const [aiOverview, setAiOverview] = useState("");
  const [aiOverviewList, setAiOverviewList] = useState([]);
  const [isListResponse, setIsListResponse] = useState(false);
  const [insufficientCreditsData, setInsufficientCreditsData] = useState(null);
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
  const [copiedRowIndex, setCopiedRowIndex] = useState(null);
  const [errors, setErrors] = useState({
    url: "",
    noQueries: "",
  });

  const firstLevelTopic = "Content Editor";
  const secondLevelTopic = "AI Overview";
  const thirdLevelTopic = "URL";

  const handleInsufficientCredits = (balanceData) => {
    setInsufficientCreditsData(balanceData);
    setShowInsufficientCredits(true);
  };

  const closeInsufficientCredits = () => {
    setShowInsufficientCredits(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "url") {
      setUrl(value);
    } else if (name === "noQueries") {
      setNoQueries(parseInt(value) || 1);
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    let error = "";
    if (name === "url") {
      if (!Validator.validateUrl(value)) {
        error = "Please enter a valid URL (e.g., https://example.com)";
      }
    } else if (name === "noQueries") {
      const num = parseInt(value);
      if (!num || num < 1 || num > 50) {
        error = "Number of queries must be between 1 and 50";
      }
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
  };

  const handleSubmit = async () => {
    if (!url.trim() || isLoading) return;

    setIsLoading(true);
    setIsResultGot(false);
    setAiOverview("");
    setErrorMessage("");

    try {
      const response = await submitAiOverviewByUrlRequest(
        { url: url, no_queries: noQueries },
        "ai_overview_url"
      );

      // Check if response is an array or list
      if (Array.isArray(response)) {
        setAiOverviewList(response);
        setIsListResponse(true);
        setAiOverview("");
      } else if (typeof response === "string") {
        setAiOverview(response);
        setIsListResponse(false);
        setAiOverviewList([]);
      } else {
        // Handle object response - could be structured data
        setAiOverview(JSON.stringify(response, null, 2));
        setIsListResponse(false);
        setAiOverviewList([]);
      }

      setIsResultGot(true);
    } catch (error) {
      console.error("Error generating AI overview:", error);
      if (error.response?.status === 402) {
        handleInsufficientCredits(error.response.data);
      } else {
        setErrorMessage(
          error.response?.data?.detail ||
            "Failed to generate AI overview. Please try again."
        );
        setShowErrorNotification(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setUrl("");
    setNoQueries(10);
    setAiOverview("");
    setAiOverviewList([]);
    setIsListResponse(false);
    setIsResultGot(false);
    setErrors({ url: "", noQueries: "" });
    setShowCopyNotification(false);
    setShowErrorNotification(false);
    setErrorMessage("");
    setCopiedRowIndex(null);
  };

  const handleCopy = async () => {
    try {
      const textToCopy = isListResponse
        ? aiOverviewList
            .map((item, index) => {
              if (typeof item === "object" && item !== null && !Array.isArray(item)) {
                const entries = Object.entries(item);
                const qaText = entries.map(([question, answer]) => `Question: ${question}\nAnswer: ${answer}`).join('\n\n');
                return `${index + 1}. ${qaText}`;
              } else if (typeof item === "string") {
                return `${index + 1}. ${item}`;
              } else {
                return `${index + 1}. ${JSON.stringify(item)}`;
              }
            })
            .join("\n\n")
        : aiOverview;
      await navigator.clipboard.writeText(textToCopy);
      setShowCopyNotification(true);
    } catch (err) {
      console.error("Failed to copy content: ", err);
    }
  };

  const handleRowCopy = async (content, index) => {
    try {
      let textToCopy;
      if (typeof content === "object" && content !== null && !Array.isArray(content)) {
        // Handle object with question as key and answer as value
        const entries = Object.entries(content);
        textToCopy = entries.map(([question, answer]) => `Question: ${question}\n\nAnswer: ${answer}`).join('\n\n---\n\n');
      } else if (typeof content === "string") {
        textToCopy = content;
      } else {
        textToCopy = JSON.stringify(content, null, 2);
      }
      
      await navigator.clipboard.writeText(textToCopy);
      setCopiedRowIndex(index);
      setShowCopyNotification(true);

      // Reset the copied row indicator after 2 seconds
      setTimeout(() => {
        setCopiedRowIndex(null);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy row content: ", err);
    }
  };

  // Form validation
  useEffect(() => {
    const isValid = url.trim() !== "" && !errors.url && !errors.noQueries;

    setIsFormValid(isValid);
  }, [url, noQueries, errors.url, errors.noQueries]);

  return (
    <div className={classNames("container-fluid", css(pageStyles.container))}>
      {/* Toast Notifications */}
      <ToastAlert
        show={showCopyNotification}
        message="AI Overview copied!"
        type="success"
        icon="fas fa-check-circle"
        duration={2000}
        onClose={() => setShowCopyNotification(false)}
        position="top-right"
      />

      <ToastAlert
        show={showErrorNotification}
        message={errorMessage}
        type="error"
        icon="fas fa-exclamation-circle"
        duration={5000}
        onClose={() => setShowErrorNotification(false)}
        position="top-right"
      />

      <h1 className="d-flex fw-normal justify-content-start align-items-center fw-bold">
        {secondLevelTopic}
      </h1>
      <p className="fw-normal justify-content-start align-items-center">
        {firstLevelTopic} / <b>{secondLevelTopic}</b> / <b>{thirdLevelTopic}</b>
      </p>
      <p className="fw-normal justify-content-start align-items-center">
        Generate comprehensive <b>AI-powered website overviews</b> by analyzing
        any URL. Get detailed insights, content strategy recommendations, and
        actionable steps to improve your website's performance and search
        visibility.
      </p>

      <div className="row">
        <div className="col-lg-9 col-md-10 col-sm-10">
          <HelpCard
            topic={"How To Use"}
            description={
              "URL Analysis generates comprehensive AI-powered website overviews by analyzing any URL. Get detailed insights, content strategy recommendations, and actionable steps to improve your website's performance and search visibility."
            }
            image={"/icon/pin.svg"}
          />
        </div>
      </div>
      <div className="p-2 mb-2 rounded">
        <div className="d-flex fw-bold mb-0">Generate AI Overview from URL</div>
        <div className="row mb-2 align-items-end">
          <div className="mb-2">
            <p className="fw-normal justify-content-start align-items-center">
              Enter the full URL of the website you want to analyze, specify the
              number of queries, and get comprehensive insights.
            </p>
            <Form.Group>
              <div className="d-flex mt-2">
                <Col sm={6} className="me-2">
                  <Form.Control
                    type="url"
                    name="url"
                    placeholder="https://example.com"
                    value={url}
                    onChange={handleInputChange}
                    isInvalid={!!errors.url}
                    onBlur={handleBlur}
                    required
                    className="px-2"
                    style={getInputFieldStyle('type6')}
                  />
                  {errors.url && (
                    <div className="text-danger small mt-1">{errors.url}</div>
                  )}
                </Col>
                <Col sm={2} className="me-2">
                  <Form.Control
                    type="number"
                    name="noQueries"
                    placeholder="Number of queries"
                    value={noQueries}
                    onChange={handleInputChange}
                    isInvalid={!!errors.noQueries}
                    onBlur={handleBlur}
                    min="1"
                    max="50"
                    className="px-2"
                    style={getInputFieldStyle('type6')}
                  />
                  {errors.noQueries && (
                    <div className="text-danger small mt-1">
                      {errors.noQueries}
                    </div>
                  )}
                </Col>
                <div className="col-lg-4 col-md-4 col-sm-12 mb-2 me-1">
                  <div className="d-flex justify-content-start align-items-end">
                    <Button
                      variant="dark"
                      className="me-1"
                      onClick={handleSubmit}
                      disabled={!isFormValid || isLoading}
                      style={getButtonStyle('type6', 'primary')}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Analyzing...
                        </>
                      ) : (
                        "Analyze"
                      )}
                    </Button>
                    <Button
                      variant="outline-dark"
                      className="me-1"
                      onClick={handleReset}
                      disabled={isLoading}
                      style={getButtonStyle('type6', 'outline')}
                    >
                      Reset
                    </Button>
                  </div>
                </div>
              </div>
            </Form.Group>
          </div>
        </div>
      </div>

      <div className="p-2">
        {url && isResultGot && (
          <div className="d-flex justify-content-between align-items-center mb-3">
            <p className="d-flex fw-bold mb-0">
              Website analysis for
              <span className="search-item">&nbsp;"{url}"</span>
            </p>
          </div>
        )}
        {isLoading && <Loader />}

        {/* Table Display for List Responses */}
        {isResultGot && isListResponse && aiOverviewList.length > 0 && (
          <AiOverviewTable
            result={aiOverviewList}
            title="Generated AI Overview"
            onCopy={() => setShowCopyNotification(true)}
          />
        )}
      </div>

      {/* Insufficient Credits Popup */}
      <InsufficientCreditsPopup
        show={showInsufficientCredits}
        onClose={closeInsufficientCredits}
        balanceData={insufficientCreditsData}
      />
    </div>
  );
}
